import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import "./SlideoutPanel.scss";
import ColourPickerContent from "./colour-picker/ColourPickerContent";
import ImageUploaderContent from "./image-uploader/ImageUploaderContent";
import FontSelectorContent from "./font-selector/FontSelectorContent";
import CustomFontsContent from "./custom-fonts/CustomFontsContent";
import AudioUploaderContent from "./audio-uploader/AudioUploaderContent";
import VideoUploaderContent from "./video-uploader/VideoUploaderContent";
import DisruptorSelectorContent from "./disruptor-selector/DisruptorSelectorContent";
import CsvUploaderContent from "./csv-uploader/CsvUploaderContent";

export const slideOutPanelControl = {
    openPanel: null,
    closePanel: null,
    setOverridePanelTitle: null,

    // Panel state is essentially the props that get passed to the content component,
    // having it here allows easy access from outside of the SlideoutPanel component
    panelState: {isOpen: false},

    // The following functions will be set inside of a useEffect hook and are specific to the colour picker content
    openColourPicker: (colour, onColourChanged, onColourSelected, title = 'Choose Color', fieldData = null) => {
        // This function is implemented in SlideoutPanel,
        if (slideOutPanelControl.openPanel) {
            slideOutPanelControl.openPanel();
        }
    },
    refreshColourPicker: (colour, fieldData) => {
        // To be implemented in SlideoutPanel,
    },
    confirmColourChange: () => {
        // To be implemented in ColourPickerContent,
    },
}

export const PANEL_CONTENT_TYPES = {
    COLOR_PICKER: 'colorPicker',
    IMAGE_UPLOADER: 'imageUploader',
    FONT_SELECTOR: 'fontSelector',
    CUSTOM_FONTS: 'customFonts',
    AUDIO_UPLOADER: 'audioUploader',
    VIDEO_UPLOADER: 'videoUploader',
    DISRUPTOR_SELECTOR: 'disruptorSelector',
    CSV_UPLOADER: 'csvUploader',
    // Future content types can be added here
};

const SlideoutPanel = (props) => {
    // Basic flag, is the panel open or closed
    const [isOpen, setIsOpen] = useState(false);

    // When the panel is fully open, can changes be made (used to prevent interaction during open/close animations)
    const [isOpenCanMakeChanges, setIsOpenCanMakeChanges] = useState(false);
    const isOpenCanMakeChangesTimeoutRef = useRef(null);

    // A callback to be called when the panel is closed
    const [onClosed, setOnClosed] = useState(null);

    // When the panel is closed, we set this flag to true after a timeout to prevent rendering the panel content (to improve performance)
    const [isClosedDontRender, setIsClosedDontRender] = useState(true);
    const isClosedDontRenderTimeoutRef = useRef(null);

    // Panel title
    const [panelTitle, setPanelTitle] = useState('Slideout Panel');
    // We can override the panel title temporarily without changing the base title,
    // this is used by the colour picker when switching to edit pallette mode
    const [overridePanelTitle, setOverridePanelTitle] = useState(null);

    // Panel content type and props, props is also stored in slideOutPanelControl.panelState for easy access but we need it here to trigger re-renders
    const [panelContentType, setPanelContentType] = useState(PANEL_CONTENT_TYPES.COLOR_PICKER);
    const [panelContentProps, setPanelContentProps] = useState({});

    useEffect(() => {
        slideOutPanelControl.closePanel = () => {
            setIsOpen(false);
            setIsOpenCanMakeChanges(false);
            clearTimeout(isOpenCanMakeChangesTimeoutRef.current);
            slideOutPanelControl.panelState.isOpen = false;
            if (onClosed) {
                onClosed();
            }
            // Delay setting isClosedDontRender to true to allow closing animation to complete
            if (isClosedDontRenderTimeoutRef.current) {
                clearTimeout(isClosedDontRenderTimeoutRef.current);
            }
            isClosedDontRenderTimeoutRef.current = setTimeout(() => {
                setIsClosedDontRender(true);
            }, 300); // Match this duration with the CSS transition duration
        }

        slideOutPanelControl.openPanel = () => {
            // Calling isOpen setter on a short timeout to make sure we see the opening animation (causes an extra re-render where the open class is applied)
            setTimeout(() => {
                setIsOpen(true);
            }, 10);
            slideOutPanelControl.panelState.isOpen = true;
            setIsClosedDontRender(false);
            clearTimeout(isClosedDontRenderTimeoutRef.current);
            setIsOpenCanMakeChanges(false);
            if (isOpenCanMakeChangesTimeoutRef.current) {
                clearTimeout(isOpenCanMakeChangesTimeoutRef.current);
            }
            isOpenCanMakeChangesTimeoutRef.current = setTimeout(() => {
                setIsOpenCanMakeChanges(true);
            }, 300); // Match this duration with the CSS transition duration
        }

        slideOutPanelControl.setOverridePanelTitle = setOverridePanelTitle;

        slideOutPanelControl.openColourPicker = (colour, onColourChanged, onColourSelected, title = 'Choose Color', fieldData = null) => {
            // console.log('SlideoutPanel: Opening colour picker with colour:', colour);
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.COLOR_PICKER,
                colour: colour,
                onColourChanged: onColourChanged,
                onColourSelected: onColourSelected,
                title: title,
                fieldData: fieldData,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.COLOR_PICKER);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.refreshColourPicker = (colour, fieldData = null) => {
            if (slideOutPanelControl.panelState.isOpen && slideOutPanelControl.panelState?.type === PANEL_CONTENT_TYPES.COLOR_PICKER && slideOutPanelControl.panelState?.fieldData === fieldData && slideOutPanelControl.panelState.canRefreshColourPicker) {
                const panelState = {
                    ...slideOutPanelControl.panelState,
                    colour: colour,
                    fieldData: fieldData,
                }
                slideOutPanelControl.panelState = panelState;
                setPanelContentProps(panelState);
            }
        }

        slideOutPanelControl.openFontSelector = (currentFont, onFontChanged = () => {}, onFontSelected = () => {}, title = 'Select Font', fieldData = null, customFonts = [], campaignId = null) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.FONT_SELECTOR,
                currentFont: currentFont,
                title: title,
                fieldData: fieldData,
                onFontChanged: onFontChanged,
                onFontSelected: onFontSelected,
                customFonts: customFonts,
                campaignId: campaignId,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.FONT_SELECTOR);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openCustomFonts = (customFonts = [], campaignId = null, fullCampaignData = null, title = 'Custom Fonts') => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.CUSTOM_FONTS,
                title: title,
                customFonts: customFonts,
                campaignId: campaignId,
                fullCampaignData: fullCampaignData,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.CUSTOM_FONTS);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openImageUploader = (imageFileData, onImageChanged = () => {}, onImageSelected = () => {}, title = 'Upload Image', fieldData = null) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.IMAGE_UPLOADER,
                imageFileData: imageFileData,
                title: title,
                fieldData: fieldData,
                onImageChanged: onImageChanged,
                onImageSelected: onImageSelected,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.IMAGE_UPLOADER);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openAudioUploader = (audioFileData, onAudioChanged = () => {}, onAudioSelected = () => {}, title = 'Upload Audio', fieldData = null, previewVolumePercent = 100) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.AUDIO_UPLOADER,
                audioFileData: audioFileData,
                title: title,
                fieldData: fieldData,
                onAudioChanged: onAudioChanged,
                onAudioSelected: onAudioSelected,
                previewVolumePercent,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.AUDIO_UPLOADER);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openVideoUploader = (videoFileData, onVideoChanged = () => {}, onVideoSelected = () => {}, title = 'Upload Video', fieldData = null) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.VIDEO_UPLOADER,
                videoFileData: videoFileData,
                title: title,
                fieldData: fieldData,
                onVideoChanged: onVideoChanged,
                onVideoSelected: onVideoSelected,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.VIDEO_UPLOADER);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openDisruptorSelector = (currentDisruptor, onDisruptorChanged = () => {}, onDisruptorSelected = () => {}, title = 'Select Disruptor', fieldData = null, customisationSchema) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.DISRUPTOR_SELECTOR,
                currentDisruptor: currentDisruptor,
                title: title,
                fieldData: fieldData,
                customisationSchema: customisationSchema,
                onDisruptorChanged: onDisruptorChanged,
                onDisruptorSelected: onDisruptorSelected,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.DISRUPTOR_SELECTOR);
            setPanelContentProps(panelState);
        }

        slideOutPanelControl.openCsvUploader = (csvUploaderData, onCsvUploaderChanged = () => {}, onCsvUploaderSelected = () => {}, title = 'CSV Uploader', fieldData = null) => {
            if (slideOutPanelControl.openPanel) {
                slideOutPanelControl.openPanel();
            }
            const panelState = {
                ...slideOutPanelControl.panelState,
                isOpen: true,
                type: PANEL_CONTENT_TYPES.CSV_UPLOADER,
                csvUploaderData: csvUploaderData,
                title: title,
                fieldData: fieldData,
                onCsvUploaderChanged: onCsvUploaderChanged,
                onCsvUploaderSelected: onCsvUploaderSelected,
            }
            slideOutPanelControl.panelState = panelState;
            setPanelTitle(title);
            setPanelContentType(PANEL_CONTENT_TYPES.CSV_UPLOADER);
            setPanelContentProps(panelState);
        }

        // Add an event listener to the document so we can detect if we click outside the panel to maybe close it or confirm changes
        const documentClickListener = (event) => {
            if (!event.target.closest('#slideoutsettingspanel') && slideOutPanelControl.panelState.isOpen) {
                // We clicked outside the panel while it is open, we don't necessarily want to close it, but we do want to confirm any changes so our undo works correctly
                if (panelContentType === PANEL_CONTENT_TYPES.COLOR_PICKER) {
                    slideOutPanelControl.confirmColourChange?.();
                }
            }
        };
        document.addEventListener('click', documentClickListener);

        return () => {
            slideOutPanelControl.closePanel = null;
            slideOutPanelControl.openPanel = null;
            slideOutPanelControl.setOverridePanelTitle = null;
            slideOutPanelControl.openColourPicker = null;
            slideOutPanelControl.refreshColourPicker = null;
            slideOutPanelControl.confirmColourChange = null;
            slideOutPanelControl.openFontSelector = null;
            slideOutPanelControl.openCustomFonts = null;
            slideOutPanelControl.openImageUploader = null;
            slideOutPanelControl.openAudioUploader = null;
            slideOutPanelControl.openVideoUploader = null;
            clearTimeout(isClosedDontRenderTimeoutRef.current);
            clearTimeout(isOpenCanMakeChangesTimeoutRef.current);
            document.removeEventListener('click', documentClickListener);
        };
    }, []);

    if (isClosedDontRender) {
        return null;
    }

    const extraStyle = props.style ? {...props.style} : {};
    if (!isOpenCanMakeChanges) {
        extraStyle.pointerEvents = 'none';
    }
    return (
        <div id="slideoutsettingspanel" className={`slideout-panel ${isOpen ? 'open' : 'closed'} ${props.className ? props.className : ''}`} style={extraStyle}>
            <div className="fl-row">
                <h5>{overridePanelTitle ? overridePanelTitle : panelTitle}</h5>
                <div className="grow"></div>
                <div className="slideout-panel-close-button" onClick={() => {slideOutPanelControl?.closePanel?.() ?? setIsOpen(false); slideOutPanelControl.panelState.isOpen = false;}}>×</div>
            </div>
            {panelContentType === PANEL_CONTENT_TYPES.COLOR_PICKER && <ColourPickerContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} showTooltipFunc={props.showTooltipFunc} hideTooltipFunc={props.hideTooltipFunc}  />}
            {panelContentType === PANEL_CONTENT_TYPES.IMAGE_UPLOADER && <ImageUploaderContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
            {panelContentType === PANEL_CONTENT_TYPES.FONT_SELECTOR && <FontSelectorContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
            {panelContentType === PANEL_CONTENT_TYPES.CUSTOM_FONTS && <CustomFontsContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
            {panelContentType === PANEL_CONTENT_TYPES.AUDIO_UPLOADER && <AudioUploaderContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
            {panelContentType === PANEL_CONTENT_TYPES.VIDEO_UPLOADER && <VideoUploaderContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
            {panelContentType === PANEL_CONTENT_TYPES.DISRUPTOR_SELECTOR && <DisruptorSelectorContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} libraryItems={props.libraryItems} selectedFeatureNode={props.selectedFeatureNode} onAddFromTemplate={props.onAddFromTemplate} featuresList={props.featuresList} />}
            {panelContentType === PANEL_CONTENT_TYPES.CSV_UPLOADER && <CsvUploaderContent {...panelContentProps} campaignData={props.campaignData} isOpen={isOpen} isOpenCanMakeChanges={isOpenCanMakeChanges} />}
        </div>
    );
}

export default SlideoutPanel;
